import { useEffect, useState } from 'react';
import { DetailViewCard } from '../../Compounds/CardWithTitle/DetailViewCard';
import {
  Asset,
  AssetUpdate,
  handleCommissionsInfoValidation,
  handleRpmAccountValidation,
  handleValidationStatusValidation,
  updateAsset
} from '../../../store/Asset';
import { RPMMiniTable } from '../../Compounds/RPM/RPMMiniTable';
import { DetailPanelContainer } from '../../Compounds/DetailPanel/DetailPanelContainer';
import { NewLinkDialog } from '../../Compounds/Linking/NewLinkDialog/NewLinkDialog';
import { DetailsLinkCTA } from '../../Compounds/DetailsLinkCTA';
import { isData, isObserver } from '../../../store/User';
import { LabelValueField } from '../../Compounds/DetailPanel/LabelValueField';
import {
  assetPayoutInfo,
  assetRpmAccountsInfo,
  assetSectionsInfo,
  assetValidationInfo,
  commissionsSectionInfo
} from './assetHelpers';
import { TicketMiniTable } from '../../Compounds/Ticket/TicketMiniTable';
import { fetchTicketsByAssetId } from '../../../store/Ticket';
import {
  fetchRpmAccountsByAsset,
  getCruxRpmAccountsByAssetLoadingDrawerSubject,
  getCruxRpmAccountsByAssetDrawerSubject
} from '../../../store/RpmAccount';
import { DetailsComments } from '../../Compounds/DetailsComments';
import { deleteLink } from '../../../store/Link';
import { getFormattedDate } from '../../../utils/helpers';
import { DetailViewValidationCard, ValidatorResult } from '../../Compounds/CardWithTitle';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { formatValidationMessages, handleRunValidator } from '../../Compounds/CardWithTitle/validationCardHelpers';
import { DecommissionParentDialog } from './DecommissionParentDialog';
import { AssetPayments } from './AssetPayments';

export interface AssetDetailsParams {
  data: Asset;
}

export const AssetDetails = ({ data }: AssetDetailsParams) => {
  const [isLinkFormOpen, setIsLinkFormOpen] = useState<boolean>(false);
  const [validationStatusBySection, setValidationStatusBySection] = useState<Record<string, ValidatorResult>>({});
  const { setSnack } = useSnackbar();

  const cardActions = () => {
    return isObserver() || isData() ? <></> : <DetailsLinkCTA setIsFormOpen={() => setIsLinkFormOpen(true)} />;
  };

  const saveAction = (update: AssetUpdate) => updateAsset(update, true);

  useEffect(() => {
    const messages = formatValidationMessages(validationStatusBySection);
    if (messages.length) {
      setSnack({
        message: messages.join(', '),
        type: 'warning',
        duration: 15000,
        open: true
      });
    }
  }, [JSON.stringify(validationStatusBySection)]);

  return (
    <div
      className="space-y-4"
      data-testid="asset-details-drawer">
      <DetailViewValidationCard
        enableCollapse
        key="commissions_detail"
        data={commissionsSectionInfo(data)}
        saveAction={(d) => saveAction(d as AssetUpdate)}
        wrapperClassName="min-h-[25rem]"
        detailValidator={(d) =>
          handleRunValidator(
            'commissionsInfo',
            d,
            handleCommissionsInfoValidation,
            validationStatusBySection,
            setValidationStatusBySection
          )
        }
        title="Commissions"
        dataTestId="commission-detail-container"
      />
      <DetailsComments
        id={data.id}
        entityType="asset"
      />
      <DetailViewCard
        enableCollapse
        key="asset_detail"
        title="Info"
        dataTestId="info-detail-container">
        <div className="min-h-[25rem]">
          <DetailPanelContainer
            data={assetSectionsInfo(data)}
            saveAction={(d) => saveAction(d as AssetUpdate)}
          />
        </div>
      </DetailViewCard>
      <DetailViewValidationCard
        enableCollapse
        key="rpm_account_detail"
        dataTestId="rpm-account-detail-container"
        data={assetRpmAccountsInfo(data)}
        cardActions={cardActions()}
        detailValidator={(d) =>
          handleRunValidator(
            'rpmAccounts',
            d,
            handleRpmAccountValidation,
            validationStatusBySection,
            setValidationStatusBySection
          )
        }
        title="RPM Accounts">
        <RPMMiniTable
          id={data.id}
          fetchFunction={fetchRpmAccountsByAsset}
          removeFunction={deleteLink}
          dataSubject={getCruxRpmAccountsByAssetDrawerSubject}
          loadingSubject={getCruxRpmAccountsByAssetLoadingDrawerSubject}
          drawer
          showRemove
        />
      </DetailViewValidationCard>
      <TicketMiniTable
        id={data.id}
        fetchFunction={fetchTicketsByAssetId}
        association={{
          type: 'asset',
          body: {
            entityId: data.id
          }
        }}
        showCreate={true}
        objectType="asset"
        objectData={data}
        objectLinkId={data.id}
        objectLinkDisplay={`Asset ${data.number}`}
        drawer
      />
      <DetailViewValidationCard
        enableCollapse
        key="asset-validation-card"
        title="Validation"
        dataTestId="validation-detail-container"
        data={assetValidationInfo(data)}
        saveAction={(d) => saveAction(d as AssetUpdate)}
        detailValidator={(d) =>
          handleRunValidator(
            'commissionsValidation',
            d,
            handleValidationStatusValidation,
            validationStatusBySection,
            setValidationStatusBySection
          )
        }
      />
      <DetailViewCard
        enableCollapse
        key="asset-payout-card"
        title="Payout"
        dataTestId="payout-detail-container">
        <div className="min-h-[5rem]">
          <DetailPanelContainer data={assetPayoutInfo(data)} />
          <AssetPayments asset={data} />
        </div>
      </DetailViewCard>
      <DetailViewCard
        enableCollapse
        key="meta_detail"
        title="Meta"
        dataTestId="meta-detail-container">
        <div className="flex px-4 pt-4 w-full">
          <LabelValueField
            label="Salesforce ID"
            value={data.sf_id}
            field="sf_id"
          />
          <LabelValueField
            label="UPSTACK ID"
            value={data.id}
            field="id"
          />
        </div>
        <div className="flex px-4 w-full">
          <LabelValueField
            label="Created At"
            value={getFormattedDate(data.createdAt, 'lll')}
            field="createdAt"
          />
          <LabelValueField
            label="Modified At"
            value={getFormattedDate(data.modifiedDate, 'lll')}
            field="modifiedDate"
          />
        </div>
      </DetailViewCard>
      <NewLinkDialog
        open={isLinkFormOpen}
        setOpen={setIsLinkFormOpen}
        asset={data}
        drawer
      />
      <DecommissionParentDialog asset={data} />
    </div>
  );
};
